/**
 * Address Book (staff) — pure presentation model (E05-F03-T03).
 *
 * Framework-free, side-effect-free helpers shared by the React page and its
 * unit tests. They encode the legacy `dashboard.js` rendering rules verbatim:
 *
 *   - the category badge label + colour map (renderStaffList);
 *   - the Add/Edit dialog default category (openStaffDialog: 'sourcer' on Add);
 *   - the Send-dialog quick-pick filter (showStaffQuickPick): active-only,
 *     filtered by category, hidden when the filtered set is empty.
 *
 * The canonical label/category constants come from `@eer/shared` so the UI and
 * the API speak ONE set of categories.
 */
import {
  STAFF_CONTACT_CATEGORIES,
  STAFF_CONTACT_CATEGORY_LABELS,
  type StaffContactCategory,
  type StaffContactDTO,
} from '@eer/shared';

/**
 * The dialog <select> options, in the legacy order
 * (Sourcer, Recruiter, Manager, Notification Recipient, Other) — T03 acceptance.
 */
export const STAFF_CATEGORY_OPTIONS: ReadonlyArray<{
  value: StaffContactCategory;
  label: string;
}> = STAFF_CONTACT_CATEGORIES.map((value) => ({
  value,
  label: STAFF_CONTACT_CATEGORY_LABELS[value],
}));

/**
 * The default category for the Add dialog. Legacy `openStaffDialog(null)` set the
 * select to `'sourcer'` for a NEW contact (T03 acceptance: "Add default
 * category=sourcer") — note this differs from the persistence default ('other',
 * used when a create omits the field entirely).
 */
export const STAFF_ADD_DEFAULT_CATEGORY: StaffContactCategory = 'sourcer';

/** Tailwind badge classes per category (renderStaffList catColors + notification). */
export const STAFF_CATEGORY_BADGE_CLASS: Record<StaffContactCategory, string> = {
  sourcer: 'bg-cyan-100 text-cyan-700',
  recruiter: 'bg-blue-100 text-blue-700',
  manager: 'bg-purple-100 text-purple-700',
  notification: 'bg-amber-100 text-amber-700',
  other: 'bg-gray-100 text-gray-600',
  research_cc: 'bg-indigo-100 text-indigo-700', // E07-F06-T10 — research Publish default CC
};

/** Badge label for a category value; unknown legacy values fall back to the raw string. */
export function categoryLabel(category: string): string {
  return STAFF_CONTACT_CATEGORY_LABELS[category as StaffContactCategory] ?? category;
}

/** Badge classes for a category value; unknown values use the neutral 'other' style. */
export function categoryBadgeClass(category: string): string {
  return (
    STAFF_CATEGORY_BADGE_CLASS[category as StaffContactCategory] ?? STAFF_CATEGORY_BADGE_CLASS.other
  );
}

/**
 * Quick-pick filter (legacy `showStaffQuickPick(filterCategory)`): keep only
 * ACTIVE contacts (`is_active !== false`), then — when a category is given —
 * those whose category matches. The caller hides the strip when the result is
 * empty (T03 acceptance: "Quick-pick только активные, фильтр по категории; пусто
 * → скрыт"). With no `filterCategory` every active contact is returned.
 */
export function filterQuickPick(
  contacts: readonly StaffContactDTO[],
  filterCategory?: string,
): StaffContactDTO[] {
  const active = contacts.filter((c) => c.is_active !== false);
  return filterCategory ? active.filter((c) => c.category === filterCategory) : active;
}
